'use client';

import { useState, useCallback } from 'react';
import { AuthForm, type AuthFormProps } from './auth-form';
import { SsoLoginForm } from './sso-login-form';

type Props = AuthFormProps;

/**
 * Login form wrapper that detects SSO-enabled email domains.
 *
 * Flow:
 * 1. User types email and submits
 * 2. Before auth, we check if the domain has SSO configured
 * 3. If SSO found → show SsoLoginForm (redirect to IdP)
 * 4. If SSO enforced → block password login
 * 5. If no SSO → proceed with normal email/password auth
 */
export function LoginWithSso(props: Props) {
  const [ssoState, setSsoState] = useState<{
    email: string;
    enforce: boolean;
  } | null>(null);
  const [checking, setChecking] = useState(false);

  const handleFormAction = useCallback(
    async (formData: FormData) => {
      const email = formData.get('email') as string;

      if (!email) {
        // Let the normal form handle validation
        return props.loginAction(formData);
      }

      // Check if domain has SSO
      setChecking(true);
      try {
        const res = await fetch('/api/auth/sso/domain-check', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });

        if (res.ok) {
          const { sso, enforce } = await res.json();
          if (sso) {
            setSsoState({ email, enforce });
            setChecking(false);
            return; // Don't proceed with password auth
          }
        }
      } catch {
        // On error, fall through to normal auth
      }
      setChecking(false);

      // No SSO — proceed with normal login
      return props.loginAction(formData);
    },
    [props],
  );

  if (ssoState) {
    return (
      <SsoLoginForm
        email={ssoState.email}
        enforce={ssoState.enforce}
        onBack={() => setSsoState(null)}
      />
    );
  }

  return (
    <AuthForm
      {...props}
      loginAction={handleFormAction as AuthFormProps['loginAction']}
    />
  );
}
